import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Stories = () => {
  const stories = [
    {
      title: "🎂 The Epic Birthday Surprise",
      date: "His Birthday",
      content: "When Dandre's birthday came around, I knew I had to do something special. So what did I do? I bought him 20 chocolate bars! You should have seen his face light up when he saw that mountain of chocolate. It wasn't just about the candy - it was about showing my best friend how much he means to me. We spent the whole day sharing those chocolates and making memories that'll last forever.",
      emoji: "🍫",
      mood: "celebration"
    },
    {
      title: "💔 The Zuzanna Incident",
      date: "A Tough Day",
      content: "There was this day when a girl named Zuzanna was giving Dandre a really hard time. I could see it was getting to him, and as his best friend, I couldn't just stand by and watch. That's when I knew exactly what to do - I went out and got him 16 chocolate bars to cheer him up. But more importantly, I stayed by his side the entire day, making sure he knew he wasn't alone.",
      emoji: "🤗",
      mood: "support"
    },
    {
      title: "🛡️ Being There When It Mattered",
      date: "Throughout the Day",
      content: "That whole day with the Zuzanna situation, I made it my mission to be the friend Dandre needed. We talked, we laughed, we shared those chocolates, and I reminded him of all the amazing things about himself. By the end of the day, he was smiling again. That's what real friendship is about - being there through the tough times and making sure your friend knows their worth.",
      emoji: "💪",
      mood: "friendship"
    },
    {
      title: "🍫 The Chocolate Tradition",
      date: "Ongoing",
      content: "Chocolate has become our thing. Whether it's celebrating victories, cheering up bad days, or just because it's Tuesday - chocolate is our friendship currency. Every bar represents a moment, a memory, a way of saying 'I've got your back.' It's not about the chocolate itself, it's about what it represents: care, thoughtfulness, and the bond we share.",
      emoji: "🎁",
      mood: "tradition"
    },
    {
      title: "🟢 The Sour Green Skittles Obsession",
      date: "Always",
      content: "If there's one thing everyone knows about Dandre, it's his absolute love for sour green skittles! He can spot a pack from across the room and will always go straight for the green ones. I've seen him trade away entire handfuls of other flavors just to get more of those tangy green gems. It's become such a signature thing that whenever I see sour green skittles, I immediately think of him. His face lights up every time he gets his hands on them!",
      emoji: "🟢",
      mood: "quirky",
      image: "/skittles.png"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const getMoodColor = (mood) => {
    switch(mood) {
      case 'celebration': return '#FFD700';
      case 'support': return '#87CEEB';
      case 'friendship': return '#98FB98';
      case 'tradition': return '#DDA0DD';
      case 'quirky': return '#32CD32';
      default: return '#fff';
    }
  };

  return (
    <div className="page-container">
      <motion.div 
        className="hero"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        style={{ padding: '2rem 0' }}
      >
        <h1>📖 Our Story</h1>
        <p>The chocolate chronicles and moments that define our friendship</p>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ marginTop: '2rem' }}
      >
        {stories.map((story, index) => (
          <motion.div
            key={index}
            variants={itemVariants}
            className="card"
            style={{ 
              marginBottom: '2rem',
              borderLeft: `4px solid ${getMoodColor(story.mood)}`,
              position: 'relative',
              overflow: 'hidden'
            }}
            whileHover={{ 
              scale: 1.02,
              boxShadow: `0 15px 40px rgba(255, 255, 255, 0.15)`
            }}
          >
            <motion.div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                fontSize: '4rem',
                opacity: 0.1,
                color: getMoodColor(story.mood)
              }}
              animate={{ 
                rotate: [0, 5, -5, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {story.emoji}
            </motion.div>
            
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ fontSize: '2rem', marginRight: '1rem' }}>{story.emoji}</span>
                <div>
                  <h2 style={{ margin: 0, color: getMoodColor(story.mood) }}>{story.title}</h2>
                  <p style={{ margin: 0, color: '#888', fontSize: '0.9rem' }}>{story.date}</p>
                </div>
              </div>
              
              <p style={{ 
                fontSize: '1.1rem', 
                lineHeight: '1.8',
                color: '#ccc'
              }}>
                {story.content}
              </p>
              
              {story.image && (
                <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                  <img 
                    src={story.image} 
                    alt="Sour Green Skittles" 
                    style={{ 
                      maxWidth: '200px', 
                      height: 'auto', 
                      borderRadius: '10px',
                      border: `2px solid ${getMoodColor(story.mood)}`,
                      boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
                    }} 
                  />
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div 
        className="card"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 1.5 }}
        style={{ 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #111 0%, #222 100%)',
          border: '2px solid #333',
          marginTop: '3rem'
        }}
      >
        <h2 style={{ marginBottom: '2rem' }}>🤝 What Makes Our Friendship Special</h2>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '2rem',
          marginBottom: '2rem'
        }}>
          <motion.div
            whileHover={{ scale: 1.05 }}
            style={{ 
              background: '#333', 
              padding: '1.5rem', 
              borderRadius: '10px',
              border: '1px solid #444'
            }}
          >
            <h3 style={{ color: '#FFD700', marginBottom: '1rem' }}>🎯 Always There</h3>
            <p>Through good times and bad, I'm always in Dandre's corner. That's what real friends do.</p>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            style={{ 
              background: '#333', 
              padding: '1.5rem', 
              borderRadius: '10px',
              border: '1px solid #444'
            }}
          >
            <h3 style={{ color: '#87CEEB', marginBottom: '1rem' }}>💝 Thoughtful Gestures</h3>
            <p>From 20 birthday chocolates to 16 comfort bars - every gesture comes from the heart.</p>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            style={{ 
              background: '#333', 
              padding: '1.5rem', 
              borderRadius: '10px',
              border: '1px solid #444'
            }}
          >
            <h3 style={{ color: '#98FB98', marginBottom: '1rem' }}>🛡️ Protective</h3>
            <p>When someone gives Dandre a hard time, yk i have to solve it bc im the goat.</p>
          </motion.div>
        </div>
        
        <motion.p 
          style={{ 
            fontSize: '1.2rem', 
            color: '#fff',
            fontWeight: 'bold',
            marginTop: '2rem'
          }}
          animate={{ 
            textShadow: [
              '0 0 5px rgba(255,255,255,0.5)',
              '0 0 10px rgba(255,255,255,0.8)',
              '0 0 5px rgba(255,255,255,0.5)'
            ]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          This is what being a great friend looks like! 🌟
        </motion.p>
      </motion.div>

      <motion.div 
        className="card"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.8 }}
        style={{ 
          background: 'linear-gradient(45deg, #1a1a1a, #2a2a2a)',
          textAlign: 'center'
        }}
      >
        <h2>📊 Friendship Stats</h2>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '1rem',
          marginTop: '2rem'
        }}>
          <div style={{ padding: '1rem' }}>
            <h3 style={{ fontSize: '3rem', color: '#FFD700', margin: 0 }}>∞</h3>
            <p style={{ color: '#ccc' }}>Total Chocolates Given</p>
          </div>
          <div style={{ padding: '1rem' }}>
            <h3 style={{ fontSize: '3rem', color: '#87CEEB', margin: 0 }}>100%</h3>
            <p style={{ color: '#ccc' }}>Support Rate</p>
          </div>
          <div style={{ padding: '1rem' }}>
            <h3 style={{ fontSize: '3rem', color: '#98FB98', margin: 0 }}>∞</h3>
            <p style={{ color: '#ccc' }}>Loyalty Level</p>
          </div>
          <div style={{ padding: '1rem' }}>
            <h3 style={{ fontSize: '3rem', color: '#DDA0DD', margin: 0 }}>24/7</h3>
            <p style={{ color: '#ccc' }}>Availability</p>
          </div>
        </div>
      </motion.div>

      <motion.div 
        className="card"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 2.1 }}
        style={{ 
          textAlign: 'center',
          marginTop: '3rem',
          background: 'linear-gradient(135deg, #2a1810, #3d2817)',
          border: '2px solid #8B4513'
        }}
      >
        <BuzzSection />
      </motion.div>
    </div>
  );
};

const BuzzSection = () => {
  const [showContent, setShowContent] = useState(false);

  return (
    <div>
      <h2 style={{ color: '#D2691E', marginBottom: '2rem' }}>🐕</h2>
      
      <motion.button
        onClick={() => setShowContent(!showContent)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        style={{
          background: 'linear-gradient(45deg, #8B4513, #D2691E)',
          color: 'white',
          border: 'none',
          padding: '15px 30px',
          borderRadius: '25px',
          fontSize: '1.2rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          boxShadow: '0 5px 15px rgba(139, 69, 19, 0.3)',
          marginBottom: '2rem'
        }}
      >
        Click Me
      </motion.button>

      {showContent && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ marginTop: '2rem' }}
        >
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: '2rem',
            alignItems: 'start'
          }}>
            <motion.div
              whileHover={{ scale: 1.02 }}
              style={{
                background: '#1a1a1a',
                padding: '1.5rem',
                borderRadius: '15px',
                border: '2px solid #8B4513'
              }}
            >
              <h3 style={{ color: '#D2691E', marginBottom: '1rem' }}>🖼️ Buzz Photo</h3>
              <img 
                src="/buz.jpg" 
                alt="Buzz" 
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  borderRadius: '10px',
                  border: '2px solid #8B4513',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
                }} 
              />
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              style={{
                background: '#1a1a1a',
                padding: '1.5rem',
                borderRadius: '15px',
                border: '2px solid #8B4513'
              }}
            >
              <h3 style={{ color: '#D2691E', marginBottom: '1rem' }}>🎥 Buzz Video</h3>
              <video 
                controls
                muted
                playsInline
                webkit-playsinline="true"
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  borderRadius: '10px',
                  border: '2px solid #8B4513',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
                }}
              >
                <source src="/buzz_video.mov" type="video/quicktime" />
                <source src="/buzz_video.mov" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </motion.div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Stories;